import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { convertImageToWebP, isImageUploadFile } from "../lib/image-webp"
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog"
import { Button } from "./ui/button"

const NORMALIZED_UPLOAD_MARKER = "webpUploadReady"

type PendingImageUpload = {
    input: HTMLInputElement
    files: File[]
}

function formatSelectedImages(imageFiles: File[]): string {
    if (imageFiles.length === 1) {
        return imageFiles[0].name
    }

    return `${imageFiles.length} selected images`
}

export function ImageUploadWebPDialog() {
    const [pendingUpload, setPendingUpload] =
        useState<PendingImageUpload | null>(null)
    const [isConverting, setIsConverting] = useState(false)
    const [conversionError, setConversionError] = useState("")

    const selectedImages = useMemo(
        () => pendingUpload?.files.filter(isImageUploadFile) ?? [],
        [pendingUpload],
    )

    useEffect(() => {
        const interceptImageUpload = (event: Event) => {
            const input = event.target
            if (!(input instanceof HTMLInputElement) || input.type !== "file") {
                return
            }

            if (input.dataset[NORMALIZED_UPLOAD_MARKER] === "true") {
                delete input.dataset[NORMALIZED_UPLOAD_MARKER]
                return
            }

            const files = Array.from(input.files ?? [])
            if (!files.some(isImageUploadFile)) {
                return
            }

            event.preventDefault()
            event.stopPropagation()
            event.stopImmediatePropagation()

            setConversionError("")
            setPendingUpload({ input, files })
        }

        document.addEventListener("change", interceptImageUpload, true)
        return () => {
            document.removeEventListener("change", interceptImageUpload, true)
        }
    }, [])

    const cancelUpload = () => {
        if (isConverting) {
            return
        }

        if (pendingUpload?.input.isConnected) {
            pendingUpload.input.value = ""
        }

        setConversionError("")
        setPendingUpload(null)
    }

    const convertAndContinue = async () => {
        if (!pendingUpload || isConverting) {
            return
        }

        if (!pendingUpload.input.isConnected) {
            setPendingUpload(null)
            return
        }

        setIsConverting(true)
        setConversionError("")

        try {
            const convertedFiles = await Promise.all(
                pendingUpload.files.map((file) =>
                    isImageUploadFile(file)
                        ? convertImageToWebP(file)
                        : Promise.resolve(file),
                ),
            )
            const transfer = new DataTransfer()
            convertedFiles.forEach((file) => transfer.items.add(file))

            pendingUpload.input.files = transfer.files
            pendingUpload.input.dataset[NORMALIZED_UPLOAD_MARKER] = "true"

            const input = pendingUpload.input
            setPendingUpload(null)
            queueMicrotask(() => {
                input.dispatchEvent(new Event("change", { bubbles: true }))
            })
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : "The selected image could not be converted to WebP."
            setConversionError(message)
            toast.error("WebP conversion failed", { description: message })
        } finally {
            setIsConverting(false)
        }
    }

    return (
        <AlertDialog
            open={Boolean(pendingUpload)}
            onOpenChange={(open) => {
                if (!open) {
                    cancelUpload()
                }
            }}
        >
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Convert images to WebP?</AlertDialogTitle>
                    <AlertDialogDescription>
                        {formatSelectedImages(selectedImages)} will be converted
                        to WebP before the upload continues. This keeps every
                        uploaded image in one consistent format.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                {conversionError ? (
                    <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {conversionError}
                    </p>
                ) : null}

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isConverting}>
                        Cancel upload
                    </AlertDialogCancel>
                    <Button
                        type="button"
                        disabled={isConverting}
                        onClick={() => void convertAndContinue()}
                    >
                        {isConverting ? "Converting..." : "Convert to WebP"}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
